import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { CookieService } from 'ngx-cookie-service';
import axios, { AxiosError } from 'axios';
import { global } from '../global';
import { SseService } from './SseService';
import { MetaApiService } from './meta-api.service';
import { WALLET_METRICS_PREFIX } from '../shared/utils/wallet-metrics.util';
import { ConfirmDialogComponent } from '../components/common/confirm-dialog/confirm-dialog.component';

/** Códigos 401 del backend (SessionInterceptor). */
export type SessionErrorCode = 'SESSION_REPLACED' | 'SESSION_EXPIRED' | 'SESSION_INVALID' | 'SESSION_REQUIRED';

const SESSION_CODES: SessionErrorCode[] = ['SESSION_REPLACED', 'SESSION_EXPIRED', 'SESSION_INVALID', 'SESSION_REQUIRED'];
const HEARTBEAT_MS = 30000;

/**
 * Sesión única por usuario (issue desarrollo#59).
 *
 * Cada login devuelve un session_id (cookie 'session_id') que viaja en la cabecera 'sesion' de toda
 * petición al backend (y como query param en las SSE). Si el usuario entra desde otro equipo, el backend
 * responde 401 {code:'SESSION_REPLACED'}: se cierra la sesión local y se avisa con un diálogo.
 * Un heartbeat cada 30 s detecta la invalidación aunque el usuario no haga nada.
 */
@Injectable({ providedIn: 'root' })
export class SessionService {
    private heartbeatTimer: any = null;
    private cerrando = false;

    constructor(
        private cookieService: CookieService,
        private router: Router,
        private dialog: MatDialog,
        private zone: NgZone,
        private sseService: SseService,
        private metaApiService: MetaApiService
    ) {
        axios.interceptors.request.use((config) => {
            const sesion = this.getSessionId();
            if (sesion && this.targetsBackend(config.url)) {
                config.headers.set('sesion', sesion);
            }
            return config;
        });
        axios.interceptors.response.use(
            (response) => response,
            (error: AxiosError) => {
                if (this.targetsBackend(error.config?.url)) {
                    this.handleErrorResponse(error.response?.status, error.response?.data);
                }
                return Promise.reject(error);
            }
        );

        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible' && this.heartbeatTimer) {
                this.checkSession();
            }
        });

        if (this.getSessionId() && this.cookieService.check('auth_token')) {
            this.startHeartbeat();
        }
    }

    getSessionId(): string {
        return this.cookieService.get('session_id');
    }

    /** Identificador estable del navegador; el backend lo guarda en users_session.mac. */
    getDeviceId(): string {
        try {
            let id = localStorage.getItem('device_id');
            if (!id) {
                id = crypto?.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
                localStorage.setItem('device_id', id);
            }
            return id;
        } catch {
            return 'sin-device-id';
        }
    }

    targetsBackend(url?: string): boolean {
        return !!url && url.startsWith(global.url);
    }

    /** Llamado por los interceptores (axios y HttpClient) con la respuesta de error del backend. */
    handleErrorResponse(status?: number, body?: any): void {
        // Sin session_id la sesión local ya está cerrada (p. ej. respuestas de peticiones que seguían en curso)
        if (status !== 401 || !this.getSessionId()) return;
        const code = body?.code as SessionErrorCode;
        if (SESSION_CODES.includes(code)) {
            this.zone.run(() => this.onSessionEnded(code));
        }
    }

    startHeartbeat(): void {
        this.stopHeartbeat();
        this.cerrando = false;
        this.heartbeatTimer = setInterval(() => this.checkSession(), HEARTBEAT_MS);
    }

    stopHeartbeat(): void {
        if (this.heartbeatTimer) {
            clearInterval(this.heartbeatTimer);
            this.heartbeatTimer = null;
        }
    }

    /** Los errores de conexión (servidor reiniciando) se ignoran; solo cierra un 401 SESSION_*. */
    private checkSession(): void {
        const licencia = this.cookieService.get('auth_token');
        if (!licencia || !this.getSessionId()) return;
        axios.get(`${global.url}session-check`, { headers: { licencia } }).catch(() => {});
    }

    /** Logout voluntario: avisa al backend (sin esperar) y limpia el estado local. */
    logout(): void {
        const licencia = this.cookieService.get('auth_token');
        if (licencia && this.getSessionId()) {
            axios.post(`${global.url}cerrar_sesion`, {}, { headers: { licencia } }).catch(() => {});
        }
        this.limpiarSesionLocal();
    }

    limpiarSesionLocal(): void {
        this.stopHeartbeat();
        this.sseService.disconnectAll();
        this.sseService.disconnect();

        [
            'auth_token', 'session_id', 'username_logged', 'userId', 'user_type_id', 'order_type_id',
            'order_type_name', 'permisos', 'oauthVerifier', 'accessToken', 'tokenSecret',
            'diffieHellmanChallenge', 'prepend', 'liveSessionToken', 'auth_broker', 'rastreador_states',
        ].forEach(n => this.cookieService.delete(n, '/'));

        this.metaApiService.clearCredentials();

        const brokerKeys = [
            'brokerConectado', 'brokerTipoBroker', 'meta_api_account_login',
            'ultima_cuenta_broker', 'iboauthmethod', 'metaApiMode', 'carterasmetaapi',
            'idcartera', 'idcarterabroker', 'idcarteragestor',
            'colaOrdenes', 'colaOrdenesGestor', 'operaciones', 'operacionesGestor',
            'positions_data', 'brokWalletMetrics', 'broker_tab_open',
            'apiIbkrAvailable', 'currentNotification'
        ];
        try {
            brokerKeys.forEach(k => localStorage.removeItem(k));
            // Keys dinámicos con prefijo (oco_<cartera>, listaordenes<cartera>, nec_rt_<symbol>, brokWalletMetrics_<cartera>)
            Object.keys(localStorage)
                .filter(k => k.startsWith('oco_') || k.startsWith('listaordenes') || k.startsWith('nec_rt_') || k.startsWith(WALLET_METRICS_PREFIX))
                .forEach(k => localStorage.removeItem(k));
        } catch { }

        this.sseService.updateLogin(false);
    }

    private onSessionEnded(code: SessionErrorCode): void {
        if (this.cerrando) return;
        this.cerrando = true;
        this.limpiarSesionLocal();

        const reemplazada = code === 'SESSION_REPLACED';
        this.dialog.closeAll();
        this.dialog.open(ConfirmDialogComponent, {
            disableClose: true,
            data: {
                icon: reemplazada ? 'devices' : 'schedule',
                titulo: reemplazada ? 'Sesión iniciada en otro equipo' : 'Tu sesión ha caducado',
                msj: reemplazada
                    ? 'Se ha iniciado sesión con tu usuario en otro equipo, así que esta sesión se ha cerrado.'
                    : 'Vuelve a iniciar sesión para continuar.',
                botonAceptar: 'Iniciar sesión',
                ocultarCancelar: true,
            },
        }).afterClosed().subscribe(() => {
            this.cerrando = false;
            this.router.navigate(['/login']);
        });
    }
}
